import type { MutableRefObject, RefObject } from "react";
import { useEffect, useRef } from "react";

interface ApplyStickyScrollStateParams {
  scrollPosition: number;
  elementTop: number;
  isSticky: boolean;
  makeSticky: () => void;
  removeSticky: () => void;
}

export function applyStickyScrollState({
  scrollPosition,
  elementTop,
  isSticky,
  makeSticky,
  removeSticky,
}: ApplyStickyScrollStateParams): void {
  if (scrollPosition >= elementTop && !isSticky) {
    makeSticky();
  } else if (scrollPosition < elementTop && isSticky) {
    removeSticky();
  }
}

export function createStickyHandlers(
  container: HTMLElement,
  inner: HTMLElement,
  topPositionRef: MutableRefObject<number>,
  isStickyRef: MutableRefObject<boolean>,
) {
  const updatePosition = () => {
    if (!isStickyRef.current) {
      topPositionRef.current = container.offsetTop;
    }
  };

  const makeSticky = () => {
    isStickyRef.current = true;
    container.style.height = `${inner.offsetHeight}px`;
    inner.style.position = "fixed";
    inner.style.top = "0";
    inner.style.left = "0";
    inner.style.right = "0";
    inner.style.zIndex = "50";
  };

  const removeSticky = () => {
    isStickyRef.current = false;
    container.style.height = "auto";
    inner.style.position = "static";
    inner.style.top = "";
    inner.style.left = "";
    inner.style.right = "";
    inner.style.zIndex = "";
  };

  return { updatePosition, makeSticky, removeSticky };
}

function useStickyTabHeader(
  containerRef: RefObject<HTMLElement | null>,
  innerRef: RefObject<HTMLElement | null>,
): void {
  const topPositionRef = useRef(0);
  const isStickyRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    const inner = innerRef.current;

    if (!container || !inner) return;

    const { updatePosition, makeSticky, removeSticky } = createStickyHandlers(
      container,
      inner,
      topPositionRef,
      isStickyRef,
    );

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          applyStickyScrollState({
            scrollPosition: window.scrollY,
            elementTop: topPositionRef.current,
            isSticky: isStickyRef.current,
            makeSticky,
            removeSticky,
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updatePosition);
    };
  }, [containerRef, innerRef]);
}

export default useStickyTabHeader;
